import { useCallback, useMemo, useState } from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import {
    FinancialDocumentExtended,
    FinancialDocumentListItem,
} from "@common/types/financialDocument";
import { useAddDocumentsToChat } from "../../useAddDocumentsToChat";
import { identity } from "../../chatDrag/identity";
import { useRowChatDrag } from "../../chatDrag/useRowChatDrag";

interface Params {
    rows: FinancialDocumentExtended[];
    detachDocuments: (documentIds: number[]) => Promise<boolean>;
}

/**
 * Bulk-selection state + actions for the GetDocuments task body table:
 * clearing selection, removing selected documents from the task, and
 * attaching selected documents as chat document references, by button or by
 * dragging rows into the chat (`rowDragOut`).
 */
export function useGetDocumentsBulkSelection({
    rows,
    detachDocuments,
}: Params) {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [isRemoving, setIsRemoving] = useState(false);
    const { canAddToChat, addDocumentsToChat } = useAddDocumentsToChat();

    const selectedDocumentIds = useMemo(
        () =>
            Object.keys(rowSelection)
                .filter((id) => rowSelection[id])
                .map((id) => Number(id)),
        [rowSelection],
    );
    const hasBulkSelection = selectedDocumentIds.length > 0;
    const selectedDocuments = useMemo(
        () => rows.filter((doc) => selectedDocumentIds.includes(doc.id)),
        [rows, selectedDocumentIds],
    );

    const handleClearBulkSelection = useCallback(() => {
        setRowSelection({});
    }, []);

    const handleBulkRemoveFromTask = useCallback(async () => {
        setIsRemoving(true);
        const removeFromTask = async () => {
            const didSucceed = await detachDocuments(selectedDocumentIds);
            if (didSucceed) {
                handleClearBulkSelection();
            }
        };
        await removeFromTask().finally(() => {
            setIsRemoving(false);
        });
    }, [detachDocuments, selectedDocumentIds, handleClearBulkSelection]);

    const handleBulkAskAbout = useCallback(() => {
        if (selectedDocuments.length === 0) {
            return;
        }
        addDocumentsToChat(selectedDocuments);
        handleClearBulkSelection();
    }, [addDocumentsToChat, selectedDocuments, handleClearBulkSelection]);

    const canAskAbout = canAddToChat && hasBulkSelection;
    const rowDragOut = useRowChatDrag({
        noun: "document",
        canAddToChat,
        getRowItem: identity<FinancialDocumentListItem>,
        selectedItems: selectedDocuments,
        addToChat: addDocumentsToChat,
        clearSelection: handleClearBulkSelection,
    });

    return {
        rowSelection,
        setRowSelection,
        selectedDocumentIds,
        hasBulkSelection,
        isRemoving,
        canAskAbout,
        handleClearBulkSelection,
        handleBulkRemoveFromTask,
        handleBulkAskAbout,
        rowDragOut,
    };
}
